import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactJson from 'react-json-view'
import _ from 'lodash';

import { useQueryClient } from '@tanstack/react-query';
import { getProjectReportLogs } from '../../../services/projectService';
import { formatTime, getErrorMessage, paginate } from '../../../utils/utilities';
import { CachePayload, restoreCachePayload } from '../../../services/reportCache';

import AccordionItem from '../../custom-reports/custom-visual-inspection/accordion-item';
import Conditional from '../../common/conditional';
import Loader from '../../themed/loader';
import NoItems from '../../blocks/no-items';
import Pagination from '../../themed/pagination';
import Select from '../../themed/select/select';

import logger from '../../../services/logService';
import classNames from './style.module.scss';
import toastService from '../../../services/toastService';


interface LogsProps {
    projectId: string;
}

interface LogItem {
    createdAt: string;
    payload: string;
}

interface LogResponse {
    message: string;
    data: LogItem[];
}

type Order = 'asc' | 'desc';

const Logs: React.FC<LogsProps> = ({ projectId }) => {
    const [logs, setLogs] = useState<LogItem[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [logSize, setLogSize] = useState(10);
    const [isLoading, setLoading] = useState(false);
    const [order, setOrder] = useState<Order>('asc');

    const queryClient = useQueryClient();

    const getLogsForProject = useCallback(async () => {
        setLoading(true);

        try {
            const response = await getProjectReportLogs<LogResponse>(projectId, logSize);
            setLogs(response.data.data);
            setLoading(false);
        } catch (error) {
            logger.log(error as Error);
            setLoading(false);
        }
    }, [projectId, logSize]);

    const handleRestoreReportCache = (payload: CachePayload) => {
        const handleClearCache = async  () => {
            await restoreCachePayload(payload);

            queryClient.invalidateQueries({
                queryKey: ['data-cache', payload.projectId],
            });

            window.location.reload();
        };

        toastService.promise(handleClearCache(), {
			loading: 'Restoring Cache',
			success: 'Cache Restored',
			error: getErrorMessage,
		});
    };

    const handleOrderChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setOrder(event.target.value as Order);
    };
   
    const handleLogSizeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setLogSize(+event.target.value);
    };

    const paginated = useMemo(() => {
        const sorted = _.orderBy(logs, ['createdAt'], [order]);
		const paginated = paginate(sorted, currentPage, PAGE_SIZE);

		return { totalCount: logs.length, paginated };
    }, [currentPage, logs, order]);

    useEffect(() => {
        getLogsForProject();
    }, [getLogsForProject]);
    return ( 
        <>
            <div className={classNames.logs}>
                <Loader isVisible={isLoading}  />

                <Conditional isVisible={!isLoading}>
                    <div className="d-flex justify-content-end align-items-center">
                        <Select options={OPTIONS} name='order' value={order} onChange={handleOrderChange}  />
                        <div className="ms-2">
                            <Select options={LOG_SIZE} name='logSize' value={logSize} onChange={handleLogSizeChange}  />
                        </div>
                    </div>

                    <Conditional isVisible={paginated.paginated.length > 0}>
                        {paginated.paginated.map((log, index) => {
                            const progress = JSON.parse(log.payload).progressData;

                            return (
                                <AccordionItem key={log.createdAt} isActive title={formatTime(log.createdAt)}>
                                    <button className={classNames.logsButton} onClick={() => handleRestoreReportCache(JSON.parse(log.payload))}>
                                        Restore
                                    </button>
        
                                    <ReactJson src={progress ? JSON.parse(JSON.parse(progress).value) : {}} />
                                </AccordionItem>
                            );
                        })}
            
                        <Pagination
                            itemsCount={paginated.totalCount}
                            pageSize={PAGE_SIZE}
                            currentPage={currentPage}
                            onPageChange={(page) => setCurrentPage(page)}
                        />
                    </Conditional>
                        
                    <NoItems isVisible={paginated.paginated.length === 0} onRefresh={getLogsForProject} />
                </Conditional>
            </div>

        </>
     );
}
 
const PAGE_SIZE = 10;

const LOG_SIZE = [
    {
        id: 5,
        name: 5
    },
    {
        id: 10,
        name: 10
    },
    {
        id: 20,
        name: 20
    },
    {
        id: 30,
        name: 30
    },
    {
        id: 32,
        name: 32
    },
    {
        id: 33,
        name: 33
    },
    {
        id: 34,
        name: 34
    },
    {
        id: 40,
        name: 40
    },
    {
        id: 50,
        name: 50
    },
    {
        id: 70,
        name: 70
    },
    {
        id: 80,
        name: 80
    },
    {
        id: 90,
        name: 90
    },
    {
        id: 100,
        name: 100
    },
];

const OPTIONS = [
    {
        id: 'desc',
        name: 'Newest to Oldest'
    },
    {
        id: 'asc',
        name: 'Oldest to Newest'
    },
];

export default Logs;